// Utility helpers for Seats.io integrations

export const DEFAULT_HOLD_COLOR = '#7f56d9';

export function parentHeader(object) {
  return object?.parent?.type === 'table' ? 'Table' : 'Row';
}

export function isWholeArea(object) {
  return object?.objectType === 'GeneralAdmissionArea' && !!object.bookAsAWhole && !object.variableOccupancy && Number(object.capacity) > 0;
}

/**
 * Determines which channel a Seats.io object belongs to by iterating through
 * configured channels and checking membership with isInChannel()
 * @param {object} object - The Seats.io object
 * @param {array} configuredChannels - Array of channel keys to check
 * @returns {string|null} The channel key the object belongs to, or null if none
 */
export function determineChannelKey(object, configuredChannels=[]) {
  if (!configuredChannels || !Array.isArray(configuredChannels)) {
    return null;
  }

  for (const channelKey of configuredChannels) {
    if (object.isInChannel(channelKey)) {
      return channelKey;
    }
  }
  return null;
}

/**
 * Seats.io objectColor callback that colors held objects based on their hold color.
 * Uses label-based lookup via extraConfig.labelToHoldConfig.
 */
export function holdObjectColor(object, defaultColor, extraConfig) {
  const holdConfig = extraConfig?.labelToHoldConfig?.[object?.label];
  if (!holdConfig) return defaultColor;
  return holdConfig.color || '#7f56d9';
}

export function openEveryNightVisible(object, extraConfig) {
  return !(extraConfig?.blockedLabels || []).includes(object?.label);
}

/**
 * Seats.io objectIcon callback that shows a lock icon on held objects.
 * Uses label-based lookup via extraConfig.labelToHoldConfig.
 * Returns FontAwesome v4.7 icon name (e.g., "lock").
 */
export function holdObjectIcon(object, defaultIcon, extraConfig) {
  const holdConfig = extraConfig?.labelToHoldConfig?.[object?.label];
  return holdConfig ? 'lock' : defaultIcon;
}

/**
 * Returns hold name popover info for a Seats.io object if it's held.
 * Uses BBCode formatting supported by Seats.io popoverInfo.
 * @param {object} object - The Seats.io object
 * @param {object} eventHolds - Object containing labelToHoldConfig mapping
 * @returns {string|null} BBCode formatted hold info, or null if not held
 */
export function getHoldPopoverInfo(object, eventHolds) {
  const holdConfig = eventHolds?.labelToHoldConfig?.[object?.label];
  if (holdConfig?.name) {
    return `[b]Hold:[/b] ${holdConfig.name}`;
  }
  return null;
}

export function allInPricePopoverInfo(object, confirm, items) {
  if (!confirm.enable_all_in_pricing || !object.category || !object.forSale || confirm.venue_ownership?.show_fee_breakdown_on_ticket_price === false) {
    return null;
  }

  const item = items.find((i) => i.seating_chart_category_key === object.category.key.toString());

  return item?.all_in_price_description ?? null;
}

/**
 * Seats.io objectColor callback that composes kill > hold > default precedence.
 * Uses label-based lookup via extraConfig.labelToKillConfig / labelToHoldConfig.
 *
 * NOTE: Seats.io stringifies render callbacks and re-evaluates them inside its
 * renderer iframe, so the body must reference only its own params and literals —
 * never module-scope helpers or constants (they are undefined in the iframe and
 * throw "ReferenceError", aborting the render). Keep this function self-contained.
 */
export function inventoryObjectColor(object, defaultColor, extraConfig) {
  if (extraConfig?.labelToKillConfig?.[object?.label]) {
    return extraConfig.killColor || '#6c757d';
  }
  const holdConfig = extraConfig?.labelToHoldConfig?.[object?.label];
  if (!holdConfig) return defaultColor;
  return holdConfig.color || '#7f56d9';
}

/**
 * Seats.io objectIcon callback. Kills get 'ban', holds get 'lock', else default.
 * Returns FontAwesome v4.7 icon name.
 *
 * Must stay self-contained — see the note on inventoryObjectColor above.
 */
export function inventoryObjectIcon(object, defaultIcon, extraConfig) {
  if (extraConfig?.labelToKillConfig?.[object?.label]) {
    return 'ban';
  }
  const holdConfig = extraConfig?.labelToHoldConfig?.[object?.label];
  return holdConfig ? 'lock' : defaultIcon;
}

export function moveToHeldPlaces(chart, object, areaPartitions) {
  const partitionId = areaPartitions && areaPartitions[object.id];
  const selected = Number(object.numSelected) || 0;
  if (!chart || !partitionId || object.objectType !== 'GeneralAdmissionArea' || selected === 0) {
    return Promise.resolve(0);
  }
  return Promise.all([chart.findObject(partitionId), chart.findObject(object.id)]).then(([partition, area]) => {
    const amount = Math.min(Number(area && area.numSelected) || 0, Number(partition && partition.numFree) || 0);
    if (amount === 0) return 0;
    return chart.deselectObjects([{ id: object.id, amount }])
      .then(() => chart.trySelectObjects([{ id: partitionId, amount }])
        .then(() => amount)
        .catch(() => chart.trySelectObjects([{ id: object.id, amount }]).then(() => 0)));
  }).catch(() => 0);
}
